import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import DateFormatter from '../utils/DateFormatter';
import NumberFormatter from '../utils/NumberFormatter';
import ProjectsCreated from './ProjectsCreated';
import TeamMemberList from './TeamMemberList';
import SubscriptionStatus from "./SubscriptionStatus";
import { getAllowedUsers, getAllowedProjects } from "../utils/CustomerUtil";
import ActivateTrialDialog from "./ActivateTrialDialog";

const TeamPage = () => {
  const { id } = useParams();
  const [teamData, setTeamData] = useState(null);
  const [allowedUsers, setAllowedUsers] = useState(null);
  const [allowedProjects, setAllowedProjects] = useState(null);

  // Define fetchTeamData with useCallback so it can be a dependency
  const fetchTeamData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/api/team/${id}`);
      setTeamData(response.data);
    } catch (error) {
      console.error("Error fetching team data:", error);
    }
  }, [id]);

  // Fetch team data on mount or when id/fetchTeamData changes
  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  // Fetch allowed users when teamData is available
  useEffect(() => {
    const fetchAllowedUsers = async () => {
      if (teamData && teamData.plan_id) {  
        const users = await getAllowedUsers(id, teamData.plan_id);
        setAllowedUsers(users);
      }
    };
  
    fetchAllowedUsers();
  }, [id, teamData]);  

  // Fetch allowed projects when teamData is available
  useEffect(() => {
    const fetchAllowedProjects = async () => {
      if (teamData && teamData.plan_id) {  
        const projects = await getAllowedProjects(id, teamData.plan_id);
        setAllowedProjects(projects);
      }
    };
    fetchAllowedProjects();
  }, [id, teamData]);  

  if (!teamData) return <div>Could not find Team {id}</div>;

  return (
    <div>
      <h2 className="heading-with-circle">
        <SubscriptionStatus
          plan={teamData.plan_name} 
          renewalDate={teamData.period_end_time} 
          price={teamData.amount_in_cents} 
        />
        Team: {teamData.name} ({teamData.team_id})
      </h2>
     
      <div className="team-container">
        <div className="subscription-info">
          <p>Current plan: <strong>{teamData.plan_name}</strong></p>
          <p>Renewing: {DateFormatter.formatDate(teamData.period_end_time)}</p>
          <p>Price: {NumberFormatter.formatCurrencyCents(teamData.amount_in_cents)}</p>      
          <p>Projects created: {teamData.charts_created} / {allowedProjects}</p>
          <p>Members: {teamData.team_members} / {allowedUsers}</p>
        </div>

        <div className="team-info">
          <p>Team Owner: <a href={`/user/${teamData.owner_user}`}>{teamData.owneremail}</a></p>
          <p>Billing contact: {teamData.billingemail}</p>
          {teamData.account_id && (
            <p>
              <a
                href={`https://highcharts.recurly.com/accounts/${teamData.account_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Recurly
              </a>
            </p>
          )}
          <ActivateTrialDialog
            teamId={teamData.team_id}
            teamName={teamData.name}
            onActivated={fetchTeamData} 
          />
        </div>
      </div>
     
      <ProjectsCreated teamId={id} />

      <TeamMemberList teamId={id} />
    </div>
  );
};

export default TeamPage;